import { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, CheckCircle2, XCircle, Plus, Filter, UserCheck, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const LEAVE_TYPES = ['Sick Leave', 'Paid Leave', 'Maternity Leave', 'Paternity Leave', 'Festive Off', 'National Holiday', 'Bereavement Leave'];

export default function LeaveManagementPage() {
  const { currentUser, isAdmin } = useApp();
  const isTeacher = currentUser?.role === 'teacher';

  const [leaves, setLeaves] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [form, setForm] = useState({ leaveType: 'Sick Leave', startDate: '', endDate: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateError, setDateError] = useState('');
  const [balances, setBalances] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => typeof window !== 'undefined' ? localStorage.getItem('sip_token') : '';

  useEffect(() => { loadLeaves(); loadBalances(); }, [statusFilter]);

  // Get existing approved/pending leave date ranges for blocking
  const bookedRanges = useMemo(() => {
    if (!isTeacher) return [];
    return leaves
      .filter(l => l.status === 'approved' || l.status === 'pending')
      .map(l => ({ start: l.startDate, end: l.endDate, type: l.leaveType, status: l.status }));
  }, [leaves, isTeacher]);

  const loadLeaves = async () => {
    setIsLoading(true);
    try {
      const data = await api.getLeaves(statusFilter === 'all' ? null : statusFilter);
      setLeaves(data);
    } catch (error) {
      toast.error('Failed to load leaves');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBalances = async () => {
    try {
      const data = await fetch(`${API_URL}/api/leaves/balances`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json());
      setBalances(data.balances || []);
    } catch { /* ignore */ }
  };

  const validateDates = (startDate, endDate) => {
    if (!startDate || !endDate) { setDateError(''); return true; }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) { setDateError('End date cannot be before start date.'); return false; }

    // Check if all days are weekends
    let hasWeekday = false;
    const d = new Date(start);
    while (d <= end) {
      if (d.getDay() !== 0 && d.getDay() !== 6) hasWeekday = true;
      d.setDate(d.getDate() + 1);
    }
    if (!hasWeekday) {
      setDateError('Leave cannot be applied for weekends only (Saturday/Sunday). Please include at least one weekday.');
      return false;
    }

    // Check overlap with existing approved/pending leaves
    for (const range of bookedRanges) {
      if (startDate <= range.end && endDate >= range.start) {
        setDateError(`Overlapping with existing ${range.type} (${range.start} to ${range.end}) — ${range.status}. Choose different dates.`);
        return false;
      }
    }

    setDateError('');
    return true;
  };

  const handleDateChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    validateDates(updated.startDate, updated.endDate);
  };

  const handleApply = async () => {
    if (!form.startDate || !form.endDate) { toast.error('Start and end dates are required'); return; }
    if (!validateDates(form.startDate, form.endDate)) { toast.error(dateError || 'Invalid dates'); return; }
    setIsSubmitting(true);
    try {
      await api.applyLeave(form);
      toast.success('Leave request submitted');
      setIsApplyOpen(false);
      setForm({ leaveType: 'Sick Leave', startDate: '', endDate: '', reason: '' });
      setDateError('');
      loadLeaves();
    } catch (error) {
      toast.error(error.message || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAction = async (leaveId, action) => {
    try {
      await api.approveLeave(leaveId, action);
      toast.success(`Leave ${action}d`);
      loadLeaves();
    } catch (error) {
      toast.error(error.message || `Failed to ${action}`);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-amber-500/10 text-amber-600',
      approved: 'bg-emerald-500/10 text-emerald-600',
      rejected: 'bg-red-500/10 text-red-600',
    };
    return <Badge className={map[status] || ''}>{status}</Badge>;
  };

  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-management-page">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Leave Management"
          description={isTeacher ? 'Apply for leave and track your requests.' : `Manage teacher leave requests. ${pendingCount ? `${pendingCount} pending.` : ''}`}
        />
        {isTeacher && (
          <Button onClick={() => setIsApplyOpen(true)} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="apply-leave-btn">
            <Plus className="w-4 h-4 mr-2" /> Apply Leave
          </Button>
        )}
      </div>

      {/* Leave Balances — for teachers */}
      {isTeacher && balances.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="leave-balances">
          {balances.map((b, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 text-center">
              <p className="text-lg font-bold text-brand-deep-blue">{b.remaining}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{b.leaveType}</p>
              <p className="text-[9px] text-muted-foreground">{b.used}/{b.quota} used</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="leave-status-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leave List */}
      <DataCard>
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" /></div>
        ) : leaves.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No leave requests found.</div>
        ) : (
          <div className="space-y-3">
            {leaves.map(leave => (
              <div key={leave.leaveId} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`leave-${leave.leaveId}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{leave.leaveType}</span>
                    {getStatusBadge(leave.status)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {isAdmin && <span className="font-medium text-foreground">{leave.teacherName} — </span>}
                    {leave.startDate} to {leave.endDate}
                    {leave.reason && <span> — {leave.reason}</span>}
                  </div>
                  {leave.reviewedByName && leave.status !== 'pending' && (
                    <div className="mt-1 text-xs flex items-center gap-1" data-testid={`leave-reviewer-${leave.leaveId}`}>
                      <UserCheck className="w-3 h-3 text-emerald-500" />
                      <span className="text-muted-foreground">{leave.status === 'approved' ? 'Approved' : 'Rejected'} by</span>
                      <span className="font-medium text-foreground">{leave.reviewedByName}</span>
                      {leave.reviewedAt && <span className="text-muted-foreground">on {new Date(leave.reviewedAt).toLocaleDateString()}</span>}
                    </div>
                  )}
                </div>
                {isAdmin && leave.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => handleAction(leave.leaveId, 'approve')} data-testid={`approve-${leave.leaveId}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleAction(leave.leaveId, 'reject')} data-testid={`reject-${leave.leaveId}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DataCard>

      {/* Apply Leave Dialog */}
      <Dialog open={isApplyOpen} onOpenChange={setIsApplyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for Leave</DialogTitle>
            <DialogDescription>Submit a new leave request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Leave Type</Label>
              <Select value={form.leaveType} onValueChange={v => setForm({...form, leaveType: v})}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={form.startDate} onChange={e => handleDateChange('startDate', e.target.value)} data-testid="leave-start" /></div>
              <div><Label>End Date</Label><Input type="date" value={form.endDate} onChange={e => handleDateChange('endDate', e.target.value)} data-testid="leave-end" /></div>
            </div>
            {dateError && (
              <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg" data-testid="leave-date-error">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-600 dark:text-red-400">{dateError}</p>
              </div>
            )}
            <div><Label>Reason (optional)</Label><Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={2} data-testid="leave-reason" /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsApplyOpen(false)}>Cancel</Button>
            <Button onClick={handleApply} disabled={isSubmitting || !!dateError} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="submit-leave-btn">
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
